import React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cn } from './cn'

// Mirrors CoreUI's `color` prop so call sites read the same at a glance,
// but renders plain Tailwind utility classes instead of Bootstrap classes.
//
// This build's Tailwind (postcss7-compat, see migration.md) predates the
// "amber/sky/emerald/..." palette expansion — only transparent/current/
// black/white/gray/red/yellow/green/blue/indigo/purple/pink are defined
// (verified via `tailwindcss/resolveConfig`). A color outside that list
// compiles to nothing (no build error, the class just matches no rule),
// which is how `warning`/`info` shipped with no background at all.
const COLOR_CLASSES = {
  primary: 'tw-bg-blue-600 tw-text-white hover:tw-bg-blue-700',
  secondary: 'tw-bg-gray-200 tw-text-gray-900 hover:tw-bg-gray-300',
  success: 'tw-bg-green-600 tw-text-white hover:tw-bg-green-700',
  danger: 'tw-bg-red-600 tw-text-white hover:tw-bg-red-700',
  warning: 'tw-bg-yellow-500 tw-text-white hover:tw-bg-yellow-600',
  info: 'tw-bg-indigo-500 tw-text-white hover:tw-bg-indigo-600',
  dark: 'tw-bg-gray-800 tw-text-white hover:tw-bg-gray-900',
  light: 'tw-bg-gray-50 tw-text-gray-900 hover:tw-bg-gray-100',
}

// CoreUI's `variant="outline"` — border + colored text on a transparent
// background instead of a solid fill. Kept as a separate literal map
// (rather than deriving classes from COLOR_CLASSES) for the same
// purge-safety reason as everywhere else in this kit: every class this
// component can render has to appear as literal text somewhere in the
// source.
const OUTLINE_COLOR_CLASSES = {
  primary: 'tw-border tw-border-blue-600 tw-text-blue-600 hover:tw-bg-blue-50',
  secondary:
    'tw-border tw-border-gray-400 tw-text-gray-700 hover:tw-bg-gray-100',
  success:
    'tw-border tw-border-green-600 tw-text-green-600 hover:tw-bg-green-50',
  danger: 'tw-border tw-border-red-600 tw-text-red-600 hover:tw-bg-red-50',
  warning:
    'tw-border tw-border-yellow-500 tw-text-yellow-600 hover:tw-bg-yellow-50',
  info: 'tw-border tw-border-indigo-500 tw-text-indigo-600 hover:tw-bg-indigo-50',
  dark: 'tw-border tw-border-gray-800 tw-text-gray-800 hover:tw-bg-gray-100',
  light: 'tw-border tw-border-gray-300 tw-text-gray-700 hover:tw-bg-gray-50',
}

const SIZE_CLASSES = {
  sm: 'tw-px-2 tw-py-1 tw-text-xs',
  md: 'tw-px-3 tw-py-1.5 tw-text-sm',
  lg: 'tw-px-4 tw-py-2 tw-text-base',
}

/**
 * Tailwind + Radix replacement for CoreUI's `<CButton>`.
 *
 * `asChild` (via Radix Slot) lets the button's styling be applied to a
 * different element (e.g. a react-router `<Link>`) without an extra
 * wrapper — the same trick shadcn/ui uses on top of Radix primitives.
 */
const Button = React.forwardRef(
  (
    {
      color = 'secondary',
      outline = false,
      size = 'md',
      block = false,
      className,
      asChild = false,
      type = 'button',
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button'
    const colorClasses = outline
      ? OUTLINE_COLOR_CLASSES[color] || OUTLINE_COLOR_CLASSES.secondary
      : COLOR_CLASSES[color] || COLOR_CLASSES.secondary
    return (
      <Comp
        ref={ref}
        type={asChild ? undefined : type}
        className={cn(
          'tw-inline-flex tw-items-center tw-justify-center tw-rounded',
          'tw-font-medium tw-transition-colors tw-cursor-pointer',
          !outline && 'tw-border-0',
          'disabled:tw-opacity-50 disabled:tw-cursor-not-allowed',
          SIZE_CLASSES[size] || SIZE_CLASSES.md,
          block && 'tw-w-full',
          colorClasses,
          className
        )}
        {...props}
      />
    )
  }
)
Button.displayName = 'Button'

export default Button
